import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api';

function pad2(n) {
  return String(n).padStart(2, '0');
}

function toYmd(d) {
  const x = new Date(d);
  return `${x.getFullYear()}-${pad2(x.getMonth() + 1)}-${pad2(x.getDate())}`;
}

export default function VenueCalendar() {
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cursor, setCursor] = useState(() => {
    const qp = search.get('date');
    if (qp) {
      const [y, m] = qp.split('-').map(Number);
      if (y && m) return new Date(y, m - 1, 1);
    }
    return new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  });

  useEffect(() => {
    setLoading(true);
    api
      .get('/events/occupancy')
      .then((r) => setRows(r.data.events || []))
      .finally(() => setLoading(false));
  }, []);

  const qpDateKey = search.get('date');
  useEffect(() => {
    if (qpDateKey && /^\d{4}-\d{2}-\d{2}$/.test(qpDateKey)) {
      const [y, m] = qpDateKey.split('-').map(Number);
      setCursor(new Date(y, m - 1, 1));
    }
  }, [qpDateKey]);

  const byYmd = useMemo(() => {
    const m = {};
    rows.forEach((ev) => {
      const k = toYmd(ev.date);
      if (!m[k]) m[k] = [];
      m[k].push(ev);
    });
    return m;
  }, [rows]);

  const selectedYmd = qpDateKey && /^\d{4}-\d{2}-\d{2}$/.test(qpDateKey) ? qpDateKey : null;
  const selectedList = selectedYmd ? byYmd[selectedYmd] || [] : [];

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const firstDow = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const pick = (day) => {
    if (!day) return;
    const ymd = `${year}-${pad2(month + 1)}-${pad2(day)}`;
    navigate(`/calendar?date=${ymd}`);
  };

  return (
    <div className="page-shell">
      <div className="page-header">
        <div>
          <div className="page-title">Venue calendar</div>
          <div className="page-subtitle">Campus-wide bookings by date — click a day to see venues in use</div>
        </div>
      </div>
      <div className="page-body">
        <div className="card" style={{ maxWidth: '56rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setCursor(new Date(year, month - 1, 1))}>
              ← Prev
            </button>
            <div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 'var(--text-lg)' }}>
              {cursor.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}
            </div>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setCursor(new Date(year, month + 1, 1))}>
              Next →
            </button>
          </div>

          {loading ? (
            <div className="page-loading" style={{ minHeight: 200 }}>
              <div className="spinner" />
            </div>
          ) : (
            <>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
                  gap: 6,
                  textAlign: 'center',
                  fontSize: 'var(--text-xs)',
                  fontWeight: 800,
                  color: 'var(--text-3)',
                  marginBottom: 6,
                  textTransform: 'uppercase',
                }}
              >
                {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((d) => (
                  <div key={d}>{d}</div>
                ))}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 6 }}>
                {cells.map((day, idx) => {
                  if (day === null) return <div key={`e-${idx}`} />;
                  const ymd = `${year}-${pad2(month + 1)}-${pad2(day)}`;
                  const list = byYmd[ymd] || [];
                  const busy = list.length > 0;
                  const isSel = selectedYmd === ymd;
                  return (
                    <button
                      key={ymd}
                      type="button"
                      onClick={() => pick(day)}
                      style={{
                        minHeight: 44,
                        borderRadius: 'var(--radius)',
                        border: `1px solid ${isSel ? 'var(--navy)' : 'var(--border-1)'}`,
                        background: busy ? 'var(--accent-muted)' : 'var(--surface-0)',
                        fontWeight: 700,
                        fontSize: 'var(--text-sm)',
                        cursor: 'pointer',
                        color: busy ? 'var(--navy-deep)' : 'var(--text-2)',
                        boxShadow: isSel ? '0 0 0 2px rgba(15,118,110,0.25)' : 'none',
                      }}
                    >
                      {day}
                      {busy && <span style={{ display: 'block', fontSize: 9, opacity: 0.85 }}>{list.length} evt</span>}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {selectedYmd && (
          <div className="card" style={{ marginTop: '1.25rem', maxWidth: '56rem' }}>
            <h2 className="card-heading" style={{ marginBottom: '0.75rem' }}>
              {new Date(selectedYmd + 'T12:00:00').toLocaleDateString('en-IN', { dateStyle: 'full' })}
            </h2>
            {selectedList.length === 0 ? (
              <p style={{ color: 'var(--text-3)', fontSize: 'var(--text-sm)' }}>No events — venues free this day.</p>
            ) : (
              <div className="stack-gap">
                {selectedList.map((ev) => (
                  <div key={ev._id} className="section-card" style={{ padding: '1rem 1.125rem' }}>
                    <div style={{ fontWeight: 800, fontFamily: 'var(--font-display)', color: 'var(--text-1)' }}>{ev.venue}</div>
                    <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-2)', marginTop: 4 }}>{ev.title}</div>
                    <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-3)', marginTop: 6 }}>
                      Host: {ev.faculty?.name || '—'} · Issued {ev.passCount || 0}/{ev.maxGuests} · Arrived {ev.arrivedCount || 0}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
